import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import type { Comment } from '@/types';

interface UseCommentsOptions {
  includeResolved?: boolean;
}

export function useComments(slug: string, options: UseCommentsOptions = {}) {
  const { includeResolved = true } = options;
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchComments = useCallback(async () => {
    if (!slug) return;

    setIsLoading(true);
    try {
      const response = await api.get(`/pages/${slug}/comments`, {
        params: { includeResolved },
      });
      if (response.data.success) {
        setComments(response.data.data.items || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch comments'));
    } finally {
      setIsLoading(false);
    }
  }, [slug, includeResolved]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  return { comments, isLoading, error, refetch: fetchComments };
}

export function useInlineComments(slug: string) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchComments = useCallback(async () => {
    if (!slug) return;

    setIsLoading(true);
    try {
      const response = await api.get(`/pages/${slug}/comments/inline`);
      if (response.data.success) {
        setComments(response.data.data || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch inline comments'));
    } finally {
      setIsLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  return { comments, isLoading, error, refetch: fetchComments };
}

interface CreateCommentData {
  content: string;
  startOffset?: number | null;
  endOffset?: number | null;
  startLine?: number | null;
  endLine?: number | null;
  selectedText?: string | null;
  parentId?: number | null;
}

export function useCreateComment(slug: string) {
  const [isPending, setIsPending] = useState(false);

  const mutate = async (data: CreateCommentData, options?: {
    onSuccess?: (comment: Comment) => void;
    onError?: (error: Error) => void;
  }) => {
    setIsPending(true);
    try {
      const response = await api.post(`/pages/${slug}/comments`, data);
      if (response.data.success) {
        options?.onSuccess?.(response.data.data);
        return response.data.data;
      }
      throw new Error(response.data.error?.message || 'Failed to create comment');
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create comment');
      options?.onError?.(error);
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return { mutate, isPending };
}

export function useDeleteComment(slug: string) {
  const [isPending, setIsPending] = useState(false);

  const mutate = async (commentId: number, options?: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  }) => {
    setIsPending(true);
    try {
      const response = await api.delete(`/pages/${slug}/comments/${commentId}`);
      if (response.data.success) {
        options?.onSuccess?.();
        return;
      }
      throw new Error(response.data.error?.message || 'Failed to delete comment');
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete comment');
      options?.onError?.(error);
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return { mutate, isPending };
}

export function useResolveComment(slug: string) {
  const [isPending, setIsPending] = useState(false);

  const mutate = async (commentId: number, resolved: boolean, options?: {
    onSuccess?: (comment: Comment) => void;
    onError?: (error: Error) => void;
  }) => {
    setIsPending(true);
    try {
      const response = await api.patch(`/pages/${slug}/comments/${commentId}/resolve`, { resolved });
      if (response.data.success) {
        options?.onSuccess?.(response.data.data);
        return response.data.data;
      }
      throw new Error(response.data.error?.message || 'Failed to resolve comment');
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to resolve comment');
      options?.onError?.(error);
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return { mutate, isPending };
}
